import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkOptional } from 'ts-essentials';

import {
  HStack,
  LucideIcon,
  Text,
  Tooltip,
  VStack,
} from '@dotfile/frontend/shared/design-system';
import {
  CheckResultEnum,
  ClientPortalAvailableDefaultLanguageEnum,
  documentAnalysisDetailedResultsDefinition,
  isClientPortalAvailableDefaultLanguage,
} from '@dotfile/shared/domain';

import { AnalysisResultReceivedExpected } from './analysis-result-received-expected';
import { AnalysisResult, AnalysisResultVariant } from './types';
import { buildAnalysisResultLabel } from './utils';

export interface DocumentAnalysisItemProps {
  analysisResult: MarkOptional<AnalysisResult, 'translation'>;
  variant?: AnalysisResultVariant;
}

export const AnalysisResultItem = ({
  analysisResult,
  variant = 'console',
}: DocumentAnalysisItemProps) => {
  const isClientPortalVariant = variant === 'client-portal';
  const textProps = {
    color: isClientPortalVariant ? 'black' : undefined,
    fontSize: isClientPortalVariant ? 'sm' : undefined,
  };
  const { i18n } = useTranslation();
  const { key, result, description, translation } = analysisResult;

  const definition = documentAnalysisDetailedResultsDefinition[result];
  const { color, iconName } = definition;
  const { label, unit } = useMemo(
    () => buildAnalysisResultLabel(analysisResult),
    [analysisResult],
  );

  const displayExpectedReceived = (
    [CheckResultEnum.rejected, CheckResultEnum.error] as CheckResultEnum[]
  ).includes(result);

  const innerItem = (
    <HStack
      cursor={
        variant === 'client-portal' ? 'unset' : description ? 'help' : 'pointer'
      }
      key={key}
      align="flex-start"
      spacing="2"
    >
      <LucideIcon mt="1" boxSize={4} color={color} name={iconName} />

      {variant === 'client-portal' ? (
        <VStack align="flex-start" alignItems="flex-start" spacing={0}>
          <Text title="" {...textProps}>
            {translation &&
            isClientPortalAvailableDefaultLanguage(i18n.language)
              ? (translation?.[i18n.language]?.description ?? description)
              : description}
          </Text>
          {displayExpectedReceived && (
            <AnalysisResultReceivedExpected
              variant={variant}
              unit={unit}
              type="received"
              analysisResult={analysisResult}
            />
          )}
          {displayExpectedReceived && (
            <AnalysisResultReceivedExpected
              variant={variant}
              unit={unit}
              type="expected"
              analysisResult={analysisResult}
            />
          )}
        </VStack>
      ) : null}

      {variant === 'console' ? (
        <VStack align="flex-start" alignItems="flex-start" spacing={0}>
          <Text title="" {...textProps} noOfLines={1}>
            {label}
          </Text>
          {displayExpectedReceived && (
            <AnalysisResultReceivedExpected
              variant={variant}
              unit={unit}
              type="received"
              analysisResult={analysisResult}
            />
          )}
          {displayExpectedReceived && (
            <AnalysisResultReceivedExpected
              variant={variant}
              unit={unit}
              type="expected"
              analysisResult={analysisResult}
            />
          )}
        </VStack>
      ) : null}
    </HStack>
  );

  if (variant === 'console' && description) {
    const label =
      translation?.[i18n.language as ClientPortalAvailableDefaultLanguageEnum]
        ?.description ?? description;
    return <Tooltip label={label}>{innerItem}</Tooltip>;
  }
  return innerItem;
};
